/**
 * Canonical segment card — clickable tile that opens the segment detail view.
 *
 * Extracted from Segments.tsx during the Maintainability Sprint 2026-04-19
 * (Task 1.1). Behavior is preserved byte-for-byte; the test coverage lives
 * at ../__tests__/Segments.test.tsx (renders name, description, customer
 * count, average revenue, "Clients" / "Revenu moyen" labels).
 */

import { Users, ChevronRight } from "lucide-react";
import { formatFCFA } from "@cieda/utils";
import type { CanonicalSegmentRow } from "@/lib/admin-api/crm";
import { SEGMENT_STYLES, DEFAULT_STYLE } from "./constants";

interface SegmentCardProps {
  segment: CanonicalSegmentRow;
  onClick: () => void;
}

export default function SegmentCard({ segment, onClick }: SegmentCardProps) {
  const style = SEGMENT_STYLES[segment.key] ?? DEFAULT_STYLE;

  return (
    <button
      onClick={onClick}
      className={`group w-full rounded-xl border p-5 text-left transition-shadow hover:shadow-md ${style.bg} ${style.border}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${style.iconBg}`}
        >
          <Users className={`h-5 w-5 ${style.text}`} />
        </div>
        <ChevronRight className="mt-1 h-4 w-4 text-stone-400 opacity-0 transition-opacity group-hover:opacity-100 shrink-0" />
      </div>

      <div className="mt-3">
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${style.bg} ${style.text}`}
        >
          {segment.name}
        </span>
        <p className="mt-1.5 text-sm text-stone-600">{segment.description}</p>
      </div>

      <div className="mt-4 flex items-end justify-between border-t border-black/5 pt-3">
        <div>
          <p className="text-xs text-stone-500">Clients</p>
          <p className={`text-xl font-display font-semibold ${style.text}`}>
            {segment.customer_count.toLocaleString("fr-SN")}
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-stone-500">Revenu moyen</p>
          <p className="text-sm font-semibold text-stone-800">
            {formatFCFA(segment.avg_revenue_fcfa)}
          </p>
        </div>
      </div>
    </button>
  );
}
